import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/ui/base/button";
import { Cookie, X } from "lucide-react";

/**
 * Aviso de cookies do portal público.
 *
 * E AVISO, nao coleta de consentimento — e o texto diz isso. Cookie essencial nao
 * depende de consentimento (docs/LGPD-E-ACESSIBILIDADE.md, secao 2: a base e
 * execucao de politica publica), e o registro formal de consentimento exigido pelo
 * critério E5.4 vive em `consentimentos_lgpd`, com finalidade, versao do aviso,
 * data e origem — coisa que esta faixa NAO faz. Prometer "autorizo o tratamento"
 * aqui seria promessa que o sistema nao cumpre.
 *
 * O endereço da política de privacidade é `/privacidade`: o prefixo `/teg` deixou
 * de existir no Prompt 6, e o link apontava para uma rota inexistente — aviso de
 * cookies com link quebrado é pior que aviso nenhum.
 */
const COOKIE_KEY = "teg-cookie-consent";

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  /**
   * Enquanto a faixa esta na tela, ela marca o <body>. E o que permite ao CSS
   * subir o botao do widget de acessibilidade, que fica na mesma quina inferior:
   * sem isso, quem mais precisa do widget nao alcanca ele ate fechar a faixa.
   */
  useEffect(() => {
    if (visible) {
      document.body.dataset.avisoDeCookies = "visivel";
    } else {
      delete document.body.dataset.avisoDeCookies;
    }
    return () => {
      delete document.body.dataset.avisoDeCookies;
    };
  }, [visible]);

  const accept = () => {
    localStorage.setItem(COOKIE_KEY, "accepted");
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(COOKIE_KEY, "dismissed");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Consentimento de cookies"
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-60 animate-fade-in"
    >
      <div className="mx-auto max-w-7xl px-4 pb-4 sm:px-6">
        <div className="rounded-xl border bg-card shadow-lg p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5 sm:mt-0" />
          <div className="flex-1 text-sm text-muted-foreground leading-relaxed">
            <p>
              Este site usa apenas cookies <strong className="text-foreground">essenciais</strong>,
              necessários para o funcionamento do serviço e para manter você conectado. Não há
              cookie de publicidade nem de medição de audiência. O tratamento de dados segue a{" "}
              <strong className="text-foreground">LGPD (Lei nº 13.709/2018)</strong> — veja as
              finalidades e os seus direitos no{" "}
              <Link to="/privacidade" className="text-link underline hover:no-underline font-medium">
                aviso de privacidade
              </Link>
              .
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={accept}>
              Entendi
            </Button>
            <Button size="sm" variant="ghost" onClick={dismiss} aria-label="Fechar aviso de cookies">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
